import type { Contract } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { fetchContract } from './fetchContract';

/**
 * Generates the query options for the contract query.
 *
 * @param contractId - The ID of the contract to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractQueryOptions = <TData = Contract>({
  contractId,
  options,
}: {
  contractId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<Contract, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: contractId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContract(contractId, { signal })
      : skipToken,
    queryKey: queryKeysContract.get(contractId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the data for a single contract by ID.
 * Returns the contract with all line items.
 *
 * @param contractId - The ID of the contract to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_contracts__contract_id__get
 */
export const useQueryContract = (
  contractId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<Contract, Error, Contract>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getContractQueryOptions<Contract>({ contractId, options }));
